import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail } from "lucide-react";
import { NAV } from "@/constants/testIds";

const links = [
  { to: "/college-tours", label: "College Tours", testId: NAV.linkTours },
  { to: "/group-travel", label: "Group Travel", testId: NAV.linkGroupTravel || "nav-link-group-travel" },
  { to: "/why-knv", label: "Why KNV", testId: NAV.linkWhy },
  { to: "/about", label: "About", testId: NAV.linkAbout },
  { to: "/contact", label: "Contact", testId: NAV.linkContact },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? "backdrop-blur-xl bg-cream/90 border-b border-ink/10 shadow-[0_8px_24px_-20px_rgba(31,26,20,0.35)]" : "bg-cream/70 backdrop-blur-md"}`}>
      {/* Utility bar — visible on ALL screens, terracotta brand color for visibility */}
      <div className="bg-terracotta text-cream" data-testid="utility-bar">
        <div className="container-edge flex items-center justify-center md:justify-between h-10 text-xs md:text-sm gap-4">
          <a href="tel:+18622354515" className="inline-flex items-center gap-2 hover:text-gold transition-colors font-medium" data-testid="utility-phone">
            <Phone size={14} className="shrink-0" /> <span>862-235-4515</span>
          </a>
          <a href="mailto:tours@knvtravelz.com" className="inline-flex items-center gap-2 hover:text-gold transition-colors font-medium" data-testid="utility-email">
            <Mail size={14} className="shrink-0" /> <span className="hidden sm:inline">tours@knvtravelz.com</span><span className="sm:hidden">Email Us</span>
          </a>
          <div className="hidden lg:block font-mono uppercase tracking-[0.18em] text-cream/80">
            Groups of 25+ · Mon–Fri 8a–6p ET
          </div>
        </div>
      </div>

      <div className="container-edge flex items-center justify-between h-20">
        <Link to="/" className="group flex items-center gap-3" data-testid={NAV.brand}>
          <img
            src="/brand/logo.jpeg"
            alt="KNV Travelz"
            className="h-14 w-14 object-contain rounded-full bg-cream"
          />
          <div className="hidden sm:block leading-tight">
            <div className="font-display font-semibold text-xl tracking-tight text-ink">KNV Travelz</div>
            <div className="font-body text-[11px] uppercase tracking-[0.22em] text-muted2">Group Tours</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testId}
              className={({ isActive }) =>
                `font-body text-sm font-medium transition-colors relative ${
                  isActive ? "text-terracotta" : "text-ink/75 hover:text-ink"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && <span className="absolute -bottom-1.5 left-0 right-0 h-[3px] bg-terracotta rounded-full" />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a href="tel:+18622354515" className="inline-flex items-center gap-2 text-ink hover:text-terracotta transition-colors text-sm font-semibold" data-testid="header-call-btn">
            <Phone size={16} /> Call
          </a>
          <Link to="/request-quote" className="btn-primary text-sm" data-testid={NAV.quoteBtn}>
            Plan My Trip
          </Link>
        </div>

        <button
          type="button"
          className="lg:hidden p-2 text-ink"
          onClick={() => setOpen((v) => !v)}
          data-testid={NAV.mobileToggle}
          aria-label="Toggle navigation"
        >
          {open ? <X size={24} strokeWidth={2} /> : <Menu size={24} strokeWidth={2} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-ink/10 bg-cream">
          <div className="container-edge py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `font-body text-base font-medium ${isActive ? "text-terracotta" : "text-ink"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="grid grid-cols-2 gap-3 mt-2">
              <a href="tel:+18622354515" className="btn-outline justify-center text-sm">
                <Phone size={16} /> Call
              </a>
              <a href="mailto:tours@knvtravelz.com" className="btn-outline justify-center text-sm">
                <Mail size={16} /> Email
              </a>
            </div>
            <Link to="/request-quote" className="btn-primary w-full">
              Plan My Trip
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
